import { IKeyboard, KeyboardListener, KeyboardState } from "../../../../client_models"

type Listener = KeyboardListener
export class Keyboard implements IKeyboard {
    public static readonly state: Map<string, boolean> = new Map()
    public static listeners: Listener[] = []
    // Set while a text field owns the keyboard (the name prompt). Keystrokes then reach only that
    // field: without this, naming a defender "Bryn" walks the player north-east and opens their
    // inventory, because every key the game reads is read from a document-level listener that
    // cannot see which DOM element has focus.
    private static typingSuppressed: boolean = false

    /**
     * Hand the keyboard to (or take it back from) a focused text field.
     *
     * Escape is deliberately NOT suppressed: it is how a player gets out of whatever is holding
     * the keyboard, so it has to keep reaching the handlers that close things.
     */
    public static suppressTyping(suppressed: boolean) {
        Keyboard.typingSuppressed = suppressed
        if (suppressed) {
            // Whatever was held when the field opened is released -- otherwise a key still down at
            // that moment would read as held for as long as the field is up (the movement loop
            // reads this map every frame, so clearing it is what stops the player walking).
            Keyboard.state.clear()
        }
    }

    private static isSuppressed(key: string): boolean {
        return Keyboard.typingSuppressed && key !== "Escape" && key !== "Esc"
    }

    public static initialize() {
        // The `.bind(this)` here isn't necesary as these functions won't use `this`!
        document.addEventListener("keydown", Keyboard.keyDown)
        document.addEventListener("keyup", Keyboard.keyUp)

        document.addEventListener("mouseleave", event => {
            if (
                event.clientY <= 0 ||
                event.clientX <= 0 ||
                event.clientX >= window.innerWidth ||
                event.clientY >= window.innerHeight
            ) {
                Keyboard.state.clear()
            }
        })
        window.addEventListener("blur", () => {
            Keyboard.state.clear()
        })
    }
    private static keyDown(e: KeyboardEvent): void {
        if (Keyboard.isSuppressed(e.key)) {
            return
        }
        const isUppercase = e.key == e.key.toUpperCase()
        Keyboard.state.delete(e.key.toLowerCase())
        Keyboard.state.delete(e.key.toUpperCase())
        Keyboard.state.set(e.key, true)
        Keyboard.listeners.forEach(listener => listener(e.key, true, e.repeat))
    }

    private static keyUp(e: KeyboardEvent): void {
        if (Keyboard.isSuppressed(e.key)) {
            return
        }
        Keyboard.state.delete(e.key.toLowerCase())
        Keyboard.state.delete(e.key.toUpperCase())
        Keyboard.state.delete(e.key)
        Keyboard.listeners.forEach(listener => listener(e.key, false))
    }

    addListener = (listener: Listener) => {
        if (Keyboard.listeners.indexOf(listener) < 0) {
            Keyboard.listeners.push(listener)
        }
    }

    removeListener = (listener: Listener) => {
        Keyboard.listeners = Keyboard.listeners.filter(l => l != listener)
    }

    state = (): KeyboardState => Keyboard.state
}
